import { v4 as uuid } from 'uuid'
import { IProcedureLogs, IToothDetails } from '@dataTypes/appointment.type'
import { toothDataToAppointmentLogEntries } from '@utils/tooth-surface-edit.utils'

export function createAppointmentProcedureRowId(): string {
  return uuid()
}

/** Stable key for matching appointment procedure rows in form state. */
export function getAppointmentProcedureRowKey(
  procedure: Partial<IProcedureLogs>,
): string {
  if (procedure.proc_log_id) {
    return procedure.proc_log_id
  }
  if (procedure.id) {
    return String(procedure.id)
  }
  return ''
}

export function findAppointmentProcedureRowIndex(
  procedures: Partial<IProcedureLogs>[] | undefined,
  rowKey: string,
): number {
  if (!procedures || !rowKey) {
    return -1
  }
  return procedures.findIndex(
    (proc) => getAppointmentProcedureRowKey(proc) === rowKey,
  )
}

/** One procedure row with all tooth/area selections from the picker. */
export function expandProcedureToToothRows(
  baseProcedure: Partial<IProcedureLogs>,
  toothData: IToothDetails[],
  options?: { primaryRowId?: string },
): Partial<IProcedureLogs>[] {
  const rowId =
    options?.primaryRowId ??
    baseProcedure.id ??
    createAppointmentProcedureRowId()
  if (toothData.length === 0) {
    return [
      {
        ...baseProcedure,
        id: rowId,
        tooth_details: [],
      },
    ]
  }
  return [
    {
      ...baseProcedure,
      id: rowId,
      tooth_details: toothDataToAppointmentLogEntries(toothData),
    },
  ]
}

export function mergeAppointmentProcedureRows(
  existing: Partial<IProcedureLogs>[] | undefined,
  incoming: Partial<IProcedureLogs>[],
  replaceRowKey?: string,
): Partial<IProcedureLogs>[] {
  const current = [...(existing ?? [])]
  incoming.forEach((row, index) => {
    const rowKey =
      index === 0 && replaceRowKey
        ? replaceRowKey
        : getAppointmentProcedureRowKey(row)
    const existingIndex = findAppointmentProcedureRowIndex(current, rowKey)
    if (existingIndex !== -1) {
      current[existingIndex] = row
    } else {
      current.push(row)
    }
  })
  return current
}
